/**
 * La bitácora: qué se pidió, qué se hizo y qué decidiste tú.
 *
 * Es la pantalla que sostiene el certificado. Todo lo demás que el Studio enseña —el gasto, las
 * llamadas— cuenta cómo se ha usado la herramienta; esto cuenta qué pasó con el libro, operación
 * por operación, con la instrucción del autor y la respuesta que le dieron.
 *
 * Dos decisiones de diseño que vienen de que esto es prueba y no un log:
 *
 *   · Si la bitácora no está pudiendo escribir, se dice ARRIBA y en primer lugar. Un registro con
 *     un agujero sigue siendo útil; uno con un agujero que nadie ve, no.
 *   · Lo que el autor no ha decidido se cuenta aparte, como «sin decidir». No se supone.
 */
import { useCallback, useEffect, useState } from "react";
import { bitacora, bitacoraResumen, decidir, dolares, miles } from "../api";
import type { Operacion, Resumen } from "../api";
import { lineaDeLectura, viasDeDecision } from "../logica";

const NOMBRES: Record<string, string> = {
  reescritura: "Reescritura", entregable: "Entregable", "informe-global": "Informe de lectura",
  informe: "Informe", lectura: "Documento abierto", subida: "Documento subido",
  ajuste: "Ajuste", arranque: "Arranque", apagado: "Apagado", decision: "Decisión",
  certificado: "Certificado", rotacion: "Bitácora rotada", purga: "Bitácora purgada",
  "clave-comprobada": "Llave comprobada", chat: "Conversación", awap: "AWAP",
  pasada: "Pasada", correccion: "Corrección", operaciones: "Operaciones", nota: "Notas y lecturas de Word",
  cambio: "Cambio de una pasada",
};

/** Lo que dice un hito de nota, con palabras del autor. */
const ACCIONES_NOTA: Record<string, string> = {
  "añadida": "Nota añadida", marcadores: "Notas llevadas a Word", comentarios: "Notas llevadas a Word, con comentarios",
  en_pasada: "Nota a la pasada", luego: "Nota para luego", no_aplica: "Nota descartada", por_decidir: "Nota vuelta a por decidir",
};

/** Los hitos de AWAP 2, con nombre para el autor. */
const TIPOS_AWAP: Record<string, string> = {
  "project.create": "Entrada en el registro", "version.seal": "Versión sellada", "declaration.sign": "Declaración de origen firmada",
};

const DECISIONES: Record<string, [string, string]> = {
  aceptado: ["Me lo quedé", "baja"], editado: ["Lo retoqué", "media"],
  descartado: ["Lo descarté", "alta"], pendiente: ["Sin revisar", "media"],
};

const hora = (ts: string) => new Date(ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
const dia = (ts: string) =>
  new Date(ts).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

/** Lo que cuenta como propuesta a decidir: lo mismo que cuenta el motor en `resumen().decisiones`. */
const ES_PROPUESTA = new Set(["reescritura", "entregable", "insercion"]);

export default function Bitacora({ carpeta, inicial = "", doc, soloPendientes = false, alSoltarPendientes, alDecidir }: {
  carpeta: string; inicial?: string; doc?: string | null;
  soloPendientes?: boolean; alSoltarPendientes?: () => void; alDecidir?: () => void;
}) {
  const [r, setR] = useState<Resumen | null>(null);
  const [ops, setOps] = useState<Operacion[]>([]);
  const [filtro, setFiltro] = useState(inicial);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [decidiendo, setDecidiendo] = useState<string | null>(null);

  // R-02: decidir desde aquí lo que quedó sin decidir, con el mismo `decidir` que usa el chat.
  async function decidirAqui(o: Operacion, d: "aceptado" | "editado" | "descartado") {
    setDecidiendo(o.id);
    try {
      await decidir(o.id, d, carpeta, o.doc ?? doc ?? undefined);
      setOps((lista) => lista.map((x) => (x.id === o.id ? { ...x, decision: { decision: d, metodo: "explicita", similitud: null, ts: new Date().toISOString() } } : x)));
      bitacoraResumen(carpeta, doc).then(setR).catch(() => {});
      alDecidir?.();
    } catch {
      setFallo("No se ha podido registrar la decisión.");
    } finally {
      setDecidiendo(null);
    }
  }

  // Rais, 21-sep: «el botón Actualizar no hace nada». Sí recargaba, pero sin nada nuevo la pantalla
  // se quedaba igual y no lo decía. Ahora dice que está leyendo y a qué hora quedó al día.
  const [leyendo, setLeyendo] = useState(false);
  const [aLas, setALas] = useState<string | null>(null);
  const cargar = useCallback(() => {
    if (!carpeta) return;
    // R0-11: en la vista general, los cambios sueltos de cada lectura de Word van resumidos en ella.
    const f: Record<string, string> = filtro ? { op: filtro } : { excluir: "cambio" };
    setLeyendo(true);
    Promise.all([bitacoraResumen(carpeta, doc), bitacora(carpeta, f)])
      .then(([res, lista]) => {
        setR(res); setOps(lista.ops); setFallo(null);
        setALas(new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      })
      .finally(() => setLeyendo(false))
      .catch(() => setFallo(
        "No he podido leer el registro de tu libro. Recarga la página; si sigue igual, vuelve a " +
        "instalar HumanInk desde humanink.io/studio.",
      ));
  }, [carpeta, filtro, doc]);

  useEffect(cargar, [cargar]);

  if (!carpeta) {
    return (
      <section className="carta">
        <h2>Bitácora<span className="sub">sin manuscrito</span></h2>
        <p className="vacio">Elige antes la carpeta de tu manuscrito: la bitácora vive junto al libro.</p>
      </section>
    );
  }

  const total = Object.values(r?.decisiones ?? {}).reduce((a, b) => a + b, 0);
  const propuestas = total - (r?.decisiones.sin_decidir ?? 0);
  const aceptado = r?.decisiones.aceptado ?? 0;

  const pendientes = ops.filter((o) => ES_PROPUESTA.has(o.op) && o.estado === "permitido" && !o.decision);
  // Un motor anterior no sabe excluir: los cambios sueltos se quitan aquí también.
  const visibles = soloPendientes ? pendientes : filtro ? ops : ops.filter((o) => o.op !== "cambio");
  const vias = viasDeDecision(r);
  const deWord = r?.porVia ? r.porVia.word.aceptado + r.porVia.word.editado + r.porVia.word.descartado : 0;

  // Agrupadas por día: es como el autor recuerda su trabajo.
  const dias: [string, Operacion[]][] = [];
  for (const o of visibles) {
    const d = dia(o.ts);
    if (dias.length && dias[dias.length - 1][0] === d) dias[dias.length - 1][1].push(o);
    else dias.push([d, [o]]);
  }

  return (
    <>
      {r?.salud && !r.salud.escribiendo && (
        <section className="carta">
          <h2>La bitácora no está escribiendo<span className="sub">desde {r.salud.desde?.slice(11, 16)}</span></h2>
          <p className="vacio" style={{ color: "var(--acc)" }}>
            {r.salud.fallos} operaciones sin registrar ({r.salud.motivo}). El trabajo sigue
            funcionando, pero lo que hagas mientras tanto no constará en el certificado.
          </p>
        </section>
      )}

      {fallo && <section className="carta"><p className="vacio">{fallo}</p></section>}

      <div className="rejilla">
        <section className="carta">
          <h2>Operaciones<span className="sub">en este libro</span></h2>
          <div className="cifra"><b>{miles(r?.operaciones ?? 0)}</b><span>registradas</span></div>
        </section>
        <section className="carta">
          <h2>Lo que te has quedado<span className="sub">de lo que decidiste</span></h2>
          <div className="cifra">
            <b>{propuestas ? Math.round((aceptado / propuestas) * 100) + " %" : "—"}</b>
            <span>{propuestas ? `de ${propuestas} ${propuestas === 1 ? "propuesta" : "propuestas"}` : "aún no has decidido nada"}</span>
          </div>
        </section>
        <section className="carta">
          <h2>Lo que ha costado<span className="sub">de tu llave</span></h2>
          <div className="cifra"><b>{dolares(r?.coste ?? 0)}</b><span>{miles(r?.tokens ?? 0)} tokens</span></div>
        </section>
      </div>

      {vias.length > 0 && (
        <section className="carta">
          <h2>Qué haces con lo que te proponen
            <span className="sub">{propuestas} {propuestas === 1 ? "decisión tuya" : "decisiones tuyas"}, las mismas que cita tu certificado</span>
          </h2>
          {vias.map((v) => (
            <div key={v.id}>
              <p className="vacio" style={{ marginTop: 10, fontWeight: 600 }}>{v.titulo}</p>
              <div className="notas">
                {(["aceptado", "editado", "descartado"] as const).map((k) => (
                  <div className="nota" key={k}>
                    <span className="et">{DECISIONES[k][0]}</span>
                    <span className="raya"><i style={{ width: `${v.decididas ? (v[k] / v.decididas) * 100 : 0}%` }} /></span>
                    <b className="mono">{v[k]}</b>
                  </div>
                ))}
              </div>
              {v.avisos.map((a) => <p className="vacio" key={a}>{a}</p>)}
            </div>
          ))}
        </section>
      )}

      {!vias.length && propuestas > 0 && (
        <section className="carta">
          <h2>Qué haces con lo que te proponen
            <span className="sub">{r?.decisiones.sin_decidir ?? 0} sin decidir</span>
          </h2>
          <div className="notas">
            {(["aceptado", "editado", "descartado"] as const).map((k) => {
              const n = r?.decisiones[k] ?? 0;
              return (
                <div className="nota" key={k}>
                  <span className="et">{DECISIONES[k][0]}</span>
                  <span className="raya"><i style={{ width: `${(n / propuestas) * 100}%` }} /></span>
                  <b className="mono">{n}</b>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="carta">
        <h2>{soloPendientes ? "Lo que falta por decidir" : "Bitácora"}
          <span className="sub">{soloPendientes ? `${pendientes.length} sin decidir` : `${ops.length} operaciones`}</span>
        </h2>
        {soloPendientes && (
          <div className="botones">
            <p className="vacio" style={{ flex: 1 }}>Lo que decidas aquí es lo que cita tu certificado.</p>
            <button className="btn texto" onClick={alSoltarPendientes}>Ver toda la bitácora</button>
          </div>
        )}
        <div className="dir">
          <select className="escribir" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="">Todo</option>
            {Object.entries(r?.porOp ?? {}).map(([k, n]) => (
              <option key={k} value={k}>{NOMBRES[k] ?? k} ({n})</option>
            ))}
          </select>
          <button className="btn fantasma" disabled={leyendo} onClick={cargar}>{leyendo ? "Leyendo…" : "Actualizar"}</button>
        </div>
        {aLas && !leyendo && <p className="vacio" aria-live="polite">Al día a las {aLas}: {ops.length} {ops.length === 1 ? "operación" : "operaciones"}.</p>}

        {!visibles.length && <p className="vacio">{soloPendientes ? "No queda nada por decidir." : "Todavía no hay nada registrado en este libro."}</p>}
        {!soloPendientes && !filtro && deWord > 0 && (
          <p className="vacio">
            Los {miles(deWord)} cambios que decidiste en Word van resumidos en cada lectura. Para verlos uno a uno,
            elige «{NOMBRES.cambio}» en la lista de arriba.
          </p>
        )}

        <div className="mapa">
          {dias.map(([d, lista]) => (
            <div key={d}>
              <div className="cap"><span className="tit" style={{ color: "var(--mut2)" }}>{d}</span></div>
              <div className="relevo">
                {lista.map((o) => (
                  <div className="paso" key={o.id}>
                    <span className="cara" style={o.estado === "permitido"
                      ? { background: "var(--azul-suave)", color: "var(--azul)" }
                      : { background: "var(--sup3)", color: "var(--mut)" }}>
                      {(o.op === "nota" && o.accion === "de_vuelta") || o.op === "cambio" ? "W" : (o.agente ?? o.op).slice(0, 2).toUpperCase()}
                    </span>
                    <span className="qu">
                      <b>{o.op === "nota" && o.accion === "de_vuelta" ? "Lo que decidiste en Word"
                        : o.op === "nota" ? (ACCIONES_NOTA[o.accion ?? ""] ?? "Nota")
                        : `${NOMBRES[o.op] ?? o.op}${o.agente ? ` · ${o.agente}` : ""}`}</b>
                      <span>{o.tipo ? `${TIPOS_AWAP[o.tipo] ?? o.tipo}${o.version ? ` · ${o.version}` : ""}`
                        : o.op === "nota" && o.accion === "de_vuelta" ? `${o.doc?.split("/").pop() ?? ""} · ${lineaDeLectura(o.meta)}`
                        : o.op === "cambio" ? `Cambio ${String(o.meta?.n ?? "")} de ${String(o.meta?.build ?? o.doc?.split("/").pop() ?? "")}`
                        : (o.entrada?.instruccion || o.doc?.split("/").pop() || o.razon || "—")}</span>
                      <span className="meta">
                        {hora(o.ts)}
                        {o.ms ? ` · ${(o.ms / 1000).toFixed(1)} s` : ""}
                        {o.coste?.cost ? ` · ${dolares(o.coste.cost)}` : ""}
                        {o.hijas ? ` · ${o.hijas} llamadas` : ""}
                        {o.estado !== "permitido" && ` · ${o.estado}`}
                        {"  "}
                        <button className="chip" onClick={() => setAbierta(abierta === o.id ? null : o.id)}>
                          {abierta === o.id ? "cerrar" : "ver"}
                        </button>
                      </span>
                      {!o.decision && ES_PROPUESTA.has(o.op) && o.estado === "permitido" && (
                        <span className="meta">
                          <button className="chip" disabled={decidiendo === o.id} onClick={() => decidirAqui(o, "aceptado")}>Me lo quedo</button>
                          <button className="chip" disabled={decidiendo === o.id} onClick={() => decidirAqui(o, "editado")}>Lo he retocado</button>
                          <button className="chip" disabled={decidiendo === o.id} onClick={() => decidirAqui(o, "descartado")}>Lo descarto</button>
                        </span>
                      )}
                      {o.decision && (
                        <span className={"grado " + (DECISIONES[o.decision.decision]?.[1] ?? "media")}>
                          {DECISIONES[o.decision.decision]?.[0] ?? o.decision.decision}
                          {o.op === "cambio" && o.decision.propio ? " · con texto tuyo" : ""}
                          {o.op === "cambio" && o.decision.metodo === "reconciliacion" ? " (leído de Word)" : o.decision.metodo !== "explicita" && " (deducido)"}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              {lista.filter((o) => o.id === abierta).map((o) => (
                <div className="issues" key={o.id + "-detalle"}>
                  <div className="issue">
                    <span className="grado media">{NOMBRES[o.op] ?? o.op}</span>
                    {o.entrada?.instruccion && <blockquote>{o.entrada.instruccion}</blockquote>}
                    {o.salida?.texto && <p style={{ whiteSpace: "pre-wrap" }}>{o.salida.texto.slice(0, 1200)}</p>}
                    <p className="vacio">
                      {o.id} · {o.estado}{o.razon ? ` · ${o.razon}` : ""}
                      {o.modelo ? ` · ${o.modelo}` : ""}
                      {o.salida?.palabras ? ` · ${miles(o.salida.palabras)} palabras` : ""}
                      {o.doc ? ` · ${o.doc}` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
